<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from "vue";
import { useI18n } from "vue-i18n";
import { McTooltip, McTag, McButton } from "@maersk-global/mds-components-core";
import { normalisedText } from "@/global";

// 🔧 Constants for layout configuration
const TAG_MAX_WIDTH = 250; // Max width allowed per tag in pixels
const TAG_GAP_DEFAULT = 8; // Default gap between tags in pixels
const RESIZE_DEBOUNCE_DELAY = 50; // Delay before resize recalculations (ms)

const props = defineProps({
  tags: {
    type: Object,
    required: true
  },
  zonePrefix: {
    type: String,
    default: ""
  }
});

const emit = defineEmits(["tag-removed", "clear-all"]);
const { t } = useI18n();

const tagsArray = computed(() => {
  const allTags = !props.zonePrefix
    ? Object.entries(props.tags).flatMap(([, tags]) => Object.entries(tags || {}).map(([, val]) => val))
    : Object.entries(props.tags[props.zonePrefix] || {}).map(([, val]) => val);

  const expanded = [];
  allTags.forEach((tag) => {
    if (Array.isArray(tag.value)) {
      tag.value.forEach((v) => {
        expanded.push({
          ...tag,
          label: v.label,
          value: v.value
        });
      });
    } else {
      expanded.push(tag);
    }
  });

  return expanded;
});

const outerContainer = ref(null);
const innerContainer = ref(null);
const measurementElement = ref(null);
const outermostContainer = ref(null);

const dynamicVisibleCount = ref(0);

const displayedTags = computed(() => tagsArray.value.slice(0, dynamicVisibleCount.value));
const shouldShowMore = computed(() => tagsArray.value.length > dynamicVisibleCount.value);

const measureTextWidthVue = (text) => {
  if (measurementElement.value) {
    measurementElement.value.textContent = text;
    // Add padding and margins that mc-tag adds
    const baseWidth = measurementElement.value.offsetWidth;
    const tagPadding = 24; // mc-tag internal padding
    const actionButtonWidth = 32; // dismiss button width
    return Math.min(baseWidth + tagPadding + actionButtonWidth, TAG_MAX_WIDTH);
  }
  return TAG_MAX_WIDTH;
};

const calculateInitialVisibleCount = () => {
  if (!outermostContainer.value || !innerContainer.value || tagsArray.value.length === 0) {
    dynamicVisibleCount.value = 0;
    return;
  }

  // Get the parent container width ([capability-name]toolbar__filter-tags-container)
  const parentContainer = outermostContainer.value.parentElement;
  if (!parentContainer) {
    dynamicVisibleCount.value = 0;
    return;
  }

  const parentWidth = parentContainer.getBoundingClientRect().width;
  const containerPadding = 24; // filter-tags-container padding

  // Find clear button more reliably
  const clearButton =
    outermostContainer.value.querySelector('[data-test="clear-filters-button"]') ||
    document.querySelector('[data-test="clear-filters-button"]');
  const clearButtonWidth = clearButton ? clearButton.getBoundingClientRect().width + 8 : 100; // 8px gap + fallback

  const computedGap = parseInt(window.getComputedStyle(innerContainer.value).gap, 10) || TAG_GAP_DEFAULT;
  const availableWidth = parentWidth - containerPadding - clearButtonWidth;
  const gap = computedGap;

  const tags = tagsArray.value;
  const visibleCount = getVisibleTagCount(tags, Math.max(200, availableWidth), gap); // minimum 200px
  dynamicVisibleCount.value = visibleCount;
};

const getTagWidth = (tag) => {
  const label = tagLabel(tag);
  // More conservative estimate for mc-tag with shadow DOM
  const estimatedWidth = Math.min(measureTextWidthVue(label), TAG_MAX_WIDTH);
  return Math.max(estimatedWidth, 80); // minimum tag width
};

const getMoreWidth = (count) => measureTextWidthVue(`+${count} more`);

const getVisibleTagCount = (tags, containerWidth, gap) => {
  if (tags.length === 0) return 0;
  if (tags.length === 1) return 1;

  let usedWidth = 0;
  let visibleCount = 0;

  for (let i = 0; i < tags.length; i++) {
    const tagWidth = getTagWidth(tags[i]);
    const nextWidth = usedWidth + (i > 0 ? gap : 0) + tagWidth;

    const remainingTags = tags.length - (i + 1);
    const needMoreTag = remainingTags > 0;
    const moreTagWidth = needMoreTag ? getMoreWidth(remainingTags) + gap : 0;

    const totalWidth = nextWidth + moreTagWidth;

    if (totalWidth + moreTagWidth > containerWidth) {
      break;
    }

    usedWidth = nextWidth;
    visibleCount++;
  }

  return visibleCount;
};

const tagLabel = (tag) => {
  const groupLabel = tag.groupLabel;
  const filterLabel = tag.filterLabel;
  const valueLabel = tag.label;
  const value = tag.value;
  const finalValueLabel = tag.normaliseText && valueLabel ? normalisedText(valueLabel) : valueLabel;

  if (groupLabel && filterLabel && finalValueLabel) {
    return `${t(groupLabel)} ${t(filterLabel)}: ${finalValueLabel}`;
  }

  if (!groupLabel && filterLabel && finalValueLabel && filterLabel !== finalValueLabel) {
    return `${t(filterLabel)}: ${finalValueLabel}`;
  }

  if (finalValueLabel) {
    return `${finalValueLabel}`;
  }

  return `${value}`;
};

const handleTagRemove = (tag) => {
  emit("tag-removed", {
    filterKey: tag.filterKey,
    value: tag.value,
    clearDependencies: tag.clearDependencies ?? [],
    extractPayloadFromOption: tag.extractPayloadFromOption
  });
};

let resizeObserver;
let resizeHandler;

onMounted(() => {
  nextTick(() => {
    calculateInitialVisibleCount();
  });

  if (outermostContainer.value) {
    resizeObserver = new ResizeObserver(() => {
      calculateInitialVisibleCount();
    });
    resizeObserver.observe(outermostContainer.value);
  }

  resizeHandler = () => {
    setTimeout(() => {
      requestAnimationFrame(() => {
        calculateInitialVisibleCount();
      });
    }, RESIZE_DEBOUNCE_DELAY);
  };

  window.addEventListener("resize", resizeHandler);
});

watch(
  () => props.tags,
  () => {
    calculateInitialVisibleCount();
  },
  { immediate: true, deep: true }
);

watch(outerContainer, (newOuterContainer) => {
  if (newOuterContainer) {
    nextTick(() => {
      if (resizeObserver) resizeObserver.disconnect();
      if (resizeObserver && outermostContainer.value) resizeObserver.observe(outermostContainer.value);
      calculateInitialVisibleCount();
    });
  }
});

onBeforeUnmount(() => {
  if (resizeObserver) resizeObserver.disconnect();
  if (resizeHandler) window.removeEventListener("resize", resizeHandler);
});
</script>

<template>
  <div ref="outermostContainer" class="filter-tags-container" data-test="filter-tags-container">
    <div ref="outerContainer" class="outer-container">
      <div ref="innerContainer" class="inner-container">
        <div
          ref="measurementElement"
          class="measurement-element"
          style="
            visibility: hidden;
            position: absolute;
            pointer-events: none;
            white-space: nowrap;
            font-family: inherit;
            font-size: 0.875rem;
            font-weight: 400;
          "
        ></div>
        <mc-tooltip v-for="tag in displayedTags" :key="`${tag.filterKey}-${tag.value}`" fit="small">
          <mc-tag
            slot="trigger"
            :key="`${tag.filterKey}-${tag.value}`"
            appearance="info"
            :data-test="`filter-tag-${tag.filterKey}`"
            withaction
            @dismiss="() => handleTagRemove(tag)"
          >
            <span class="tag-content">{{ tagLabel(tag) }}</span>
          </mc-tag>
          <slot>
            <span>{{ tagLabel(tag) }}</span>
          </slot>
        </mc-tooltip>

        <mc-tooltip position="bottom">
          <mc-tag
            v-if="shouldShowMore"
            slot="trigger"
            appearance="info"
            data-test="more-tags"
            :label="`+${tagsArray.length - displayedTags.length} ${t('MORE')}`"
          ></mc-tag>
          <slot>
            <div class="morefilters-popover-container">
              <mc-tag
                v-for="tag in tagsArray.slice(displayedTags.length)"
                :key="`more-${tag.filterKey}-${tag.value}`"
                appearance="info"
                :data-test="`more-filter-tag-${tag.filterKey}`"
                withaction
                @dismiss="() => handleTagRemove(tag)"
              >
                <span class="tag-content-popover">{{ tagLabel(tag) }}</span>
              </mc-tag>
            </div>
          </slot>
        </mc-tooltip>
      </div>
    </div>
    <mc-button
      v-if="tagsArray.length > 0"
      data-test="clear-filters-button"
      :label="t('CLEAR_FILTERS')"
      variant="plain"
      appearance="neutral"
      fit="small"
      @click="() => emit('clear-all')"
    ></mc-button>
  </div>
</template>

<style scoped lang="scss">
.filter-tags-container {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 12px;
  min-width: 0;
  box-sizing: border-box;
  justify-content: flex-start;
}

.outer-container {
  overflow: hidden;
  min-width: 0;
  width: fit-content;
}

.inner-container {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  width: fit-content;
  max-width: 100%;
}

.measurement-element {
  font-family: inherit;
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 1.25;
  letter-spacing: 0;
}

.tag,
.more-tags .temp-measurement {
  background-color: #eee;
  color: #333;
  padding: 6px 10px;
  border-radius: 4px;
  font-size: 0.875rem;
  white-space: nowrap;
}

.more-tags {
  background-color: #ccc;
  cursor: pointer;
}

.morefilters-popover-container {
  width: fit-content;
  max-width: 400px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 6px;
}
.tag-content {
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
}
.tag-content-popover {
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.tag-content-popover:hover {
  white-space: normal;
  overflow-wrap: break-word;
}
mc-tag::part(tag) {
  height: fit-content;
}
.temp-measurement {
  position: absolute;
  visibility: hidden;
  pointer-events: none;
  z-index: -1;
}
</style>
